import {
  Chip,
  EllipsisMenu,
  FormatInformation,
  Prompt,
  Typography,
  SnackbarStatus,
  useSnackbar,
  EditIcon,
  Bin,
  IOption,
  DetailsPanel,
} from '@weareopenr/component-library';
import styles from './RulesCard.module.scss';
import { ItemRule } from 'src/types/menu.types';
import { getRulesCount } from './getRulesCount/getRulesCount';
import ImageIcon from 'src/assets/images/ImageIcon';
import { useState } from 'react';
import { deleteAttributeGroup } from 'src/api/deleteRule/deleteAttributeGroup';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../hooks/QueryKeys';

interface RulesCardProps {
  rule: ItemRule;
  defaultAttributes: {
    consumer_name: string;
    description: string;
    plu: string;
    imageUrl: string;
  };
  externalSystemName: string;
  menuItemId: string;
  setIsChannelAttributeGroupOpen: (value: boolean) => void;
  setRuleToEdit: () => void;
}

const NoImage = () => {
  return (
    <div className={styles['no-image']}>
      <ImageIcon />
      <Typography className={styles['image-title']} size="medium">
        No Image
      </Typography>
    </div>
  );
};

export const RulesCard: React.FC<RulesCardProps> = ({
  rule,
  defaultAttributes,
  externalSystemName,
  setIsChannelAttributeGroupOpen,
  setRuleToEdit,
  menuItemId,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const refetchItem = async () => {
    await queryClient.invalidateQueries({ queryKey: [QueryKeys.item, menuItemId] });
  };
  const rulesCount = getRulesCount(rule);
  const consumerName = rule.consumer_name || defaultAttributes.consumer_name;
  const description = rule.description || defaultAttributes.description;
  const plu = rule.plu || defaultAttributes.plu;
  const imageUrl = rule.assets.length > 0 ? rule.assets[0].asset_variants[0].url : defaultAttributes.imageUrl;
  const pillLabel = 'Channel specific';

  const onEditClick = () => {
    setRuleToEdit();
    setIsChannelAttributeGroupOpen(true);
  };

  const ellipsisOptions: IOption[] = [
    {
      id: 1,
      name: 'Edit',
      icon: EditIcon,
      onOptionClick: onEditClick,
      dataTestId: 'edit-button',
    },
    {
      id: 2,
      name: 'Delete',
      icon: Bin,
      onOptionClick: () => setShowDeleteModal(true),
      hoverColour: 'tomato',
      dataTestId: 'delete-button',
    },
  ];

  const handleDeleteRule = async (): Promise<void> => {
    try {
      await deleteAttributeGroup(menuItemId, rule.external_system_id);
      refetchItem();
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: 'Attribute group deleted',
      });
    } catch {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'Unable to delete attribute group. Please try again',
      });
    } finally {
      setShowDeleteModal(false);
    }
  };

  return (
    <>
      <Prompt
        promptHeading={'Delete attribute group?'}
        closePrompt={() => setShowDeleteModal(false)}
        open={showDeleteModal}
        buttonHandler={handleDeleteRule}
        buttonText="Delete"
        backButtonText="Cancel"
        backButton={true}
        size="small"
      >
        This action cannot be undone.
      </Prompt>
      <DetailsPanel
        title={`${externalSystemName} (${rulesCount})`}
        action={<EllipsisMenu variant="ghost" options={ellipsisOptions} />}
      >
        <DetailsPanel.Row>
          <DetailsPanel.Column>
            <FormatInformation
              chip={rule.consumer_name && <Chip label={pillLabel} backgroundColour="blueberry" />}
              heading="Consumer Name"
            >
              {consumerName || 'No Consumer Name'}
            </FormatInformation>
            <FormatInformation
              chip={rule.description && <Chip label={pillLabel} backgroundColour="blueberry" />}
              heading="Description"
            >
              {description || 'No Description'}
            </FormatInformation>
            <FormatInformation
              chip={rule.plu && <Chip label={pillLabel} backgroundColour="blueberry" />}
              heading="POS ID"
            >
              {plu || 'No POS ID'}
            </FormatInformation>
          </DetailsPanel.Column>

          <DetailsPanel.Column>
            <FormatInformation
              heading="Image"
              chip={rule.assets.length > 0 && <Chip label={pillLabel} backgroundColour="blueberry" />}
            >
              {imageUrl ? <img className={styles['image']} src={imageUrl} alt="Product Image" /> : <NoImage />}
            </FormatInformation>
          </DetailsPanel.Column>
        </DetailsPanel.Row>
      </DetailsPanel>
    </>
  );
};
